<script setup lang="ts">
import type { ToastProps } from "./toast.types";
import { useToast } from "./useToast";

const props = withDefaults(defineProps<ToastProps>(), {
  type: "default",
  duration: 4000,
  dismissible: true,
  closeButton: undefined,
  richColors: false,
});

const {
  cardColor,
  cardVariant,
  iconName,
  toastCount,
  descriptionClamp,
  ariaRole,
  ariaLive,
  showCloseButton,
  isRemoving,
  baseClasses,
  swipeStyle,
  handleMouseEnter,
  handleMouseLeave,
  handleClose,
  handlePointerDown,
  handlePointerMove,
  handlePointerUp,
} = useToast({ props });
</script>

<template>
  <div
    part="base"
    :class="baseClasses"
    :data-type="props.type"
    :data-removing="isRemoving || undefined"
    :style="swipeStyle"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
  >
    <e-chip
      v-if="toastCount"
      :text="toastCount"
      :color="cardColor"
      variant="subtle"
      size="xs"
      square
      style="position: absolute; top: 0; right: 0; z-index: 10; translate: 50% -50%"
    />
    <e-card size="xs" :color="cardColor" :variant="cardVariant">
      <div :role="ariaRole" :aria-live="ariaLive" :aria-atomic="true">
        <div class="flex items-center gap-3">
          <span v-show="iconName" class="shrink-0">
            <slot name="icon">
              <e-icon :name="iconName" size="sm" />
            </slot>
          </span>

          <div v-if="title" class="min-w-0 flex-1 text-sm font-medium">
            {{ title }}
          </div>

          <span v-if="showCloseButton" class="shrink-0" @pointerdown.stop @pointerup.stop="handleClose">
            <e-button size="xs" square icon="close" variant="ghost" aria-label="Close" />
          </span>
        </div>

        <div
          v-if="description"
          class="mt-1 text-sm opacity-70"
          :class="[descriptionClamp]"
          :style="iconName ? 'margin-inline-start: calc(var(--e-icon-size-sm, 16px) + 12px)' : undefined"
        >
          {{ description }}
        </div>
        <slot />
      </div>
    </e-card>
  </div>
</template>

<style>
:host {
  display: block;
}
</style>
